import { getTokenImageUrl } from '@/domain/euler'
import { getDisplayTokenSymbol } from '@/domain/hooks/useDisplayToken'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import type {
  ApyBreakdown,
  ApyPointsInfo,
  MarketEntryViewModel,
  MarketExposureVM,
  MarketValue,
} from '@/ui/organisms/market-entry/types'
import type { YieldExposure, YieldOpportunity } from '../types'
import { getMarketType, getUtilization, getYieldOpportunityDetailsPath } from '../utils'

function getApyBreakdown(opp: YieldOpportunity): ApyBreakdown {
  if (opp.sourceData.type === 'elend') {
    return {
      lendingApy: opp.sourceData.lendingAPY ?? opp.supplyAPY,
      intrinsicApy: opp.sourceData.intrinsicAPY,
      totalApy: opp.supplyAPY,
    }
  }
  if (opp.sourceData.type === 'pooled') {
    return {
      lendingApy: opp.sourceData.lendingAPY ?? opp.supplyAPY,
      intrinsicApy: opp.sourceData.intrinsicAPY,
      totalApy: opp.supplyAPY,
    }
  }
  return {
    lendingApy: opp.supplyAPY,
    intrinsicApy: undefined,
    totalApy: opp.supplyAPY,
  }
}

const DEFAULT_CURATOR_LOGO = '/hypurr-paw.svg'
const MIN_DISPLAY_USD = 0.01

function formatUsd(value: number | undefined, compact = true): string {
  if (value === undefined || value === 0) return '-'
  if (compact) {
    if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
    if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`
    return `$${value.toFixed(2)}`
  }
  return `$${value.toFixed(2)}`
}

function formatTokenAmount(value: number | undefined, symbol: string, decimals = 2): string {
  if (value === undefined || value === 0) return '-'
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(2)}B ${symbol}`
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M ${symbol}`
  if (value >= 1_000) return `${(value / 1_000).toFixed(2)}K ${symbol}`
  return `${value.toFixed(decimals)} ${symbol}`
}

function getTotalAssets(opp: YieldOpportunity): number | undefined {
  switch (opp.sourceData.type) {
    case 'evault':
    case 'elend':
    case 'pooled':
    case 'isolated':
      return opp.sourceData.totalAssets?.toNumber()
    default:
      return undefined
  }
}

function toExposureVM(exposure: YieldExposure | null | undefined): MarketExposureVM {
  if (exposure === null) {
    return { kind: 'none', label: '-', description: 'Exposure data not available in fallback mode', icon: 'dash' }
  }
  if (!exposure) {
    return { kind: 'none', label: '-', description: 'No exposure data available', icon: 'dash' }
  }

  switch (exposure.type) {
    case 'shared-pool':
      return {
        kind: 'shared-pool',
        description: 'Shared lending pool with multiple collateral types',
      }

    case 'isolated-pair':
      if (exposure.isolatedPair) {
        return {
          kind: 'isolated-pair',
          collateral: {
            symbol: exposure.isolatedPair.collateralToken.symbol.toString(),
            logoSrc: exposure.isolatedPair.collateralToken.logoURI,
          },
          maxLtv: `${(exposure.isolatedPair.maxLtv.toNumber() * 100).toFixed(0)}%`,
        }
      }
      return { kind: 'none', label: '-', description: 'Isolated pair data unavailable', icon: 'dash' }

    case 'vault-collaterals':
      if (exposure.collaterals && exposure.collaterals.length > 0) {
        const items = exposure.collaterals.slice(0, 4).map((col) => ({
          key: col.collateralAddress,
          name: col.name,
          logoSrc: undefined,
          tooltipLines: [
            ...(col.borrowLTV !== undefined
              ? [{ label: 'Borrow LTV', value: `${(col.borrowLTV / 100).toFixed(0)}%` }]
              : []),
            ...(col.liquidationLTV !== undefined
              ? [{ label: 'Liq. LTV', value: `${(col.liquidationLTV / 100).toFixed(0)}%` }]
              : []),
          ],
        }))
        return {
          kind: 'stack',
          items,
          overflowCount: exposure.collaterals.length > 4 ? exposure.collaterals.length - 4 : undefined,
        }
      }
      return { kind: 'none', label: '-', description: 'No collaterals configured', icon: 'x' }

    case 'vault-strategies':
      if (exposure.strategies && exposure.strategies.length > 0) {
        const items = exposure.strategies.slice(0, 4).map((strat) => ({
          key: strat.assetAddress || strat.collateralAddress,
          name: strat.name,
          logoSrc: undefined,
          tooltipLines: [
            ...(strat.allocationUsd !== undefined
              ? [{ label: 'Allocation', value: formatUsd(strat.allocationUsd, true) }]
              : []),
            ...(strat.supplyAPY !== undefined ? [{ label: 'APY', value: `${strat.supplyAPY.toFixed(2)}%` }] : []),
          ],
        }))
        return {
          kind: 'stack',
          items,
          overflowCount: exposure.strategies.length > 4 ? exposure.strategies.length - 4 : undefined,
        }
      }
      return { kind: 'none', label: '-', description: 'No strategies configured', icon: 'x' }

    default:
      return { kind: 'none', label: '-', description: 'Unknown exposure type', icon: 'dash' }
  }
}

export function toMarketEntryViewModel(opp: YieldOpportunity): MarketEntryViewModel {
  const tokenImage = getTokenImageUrl(opp.tokenInfo, opp.assetSymbol)
  const fallbackLogo = opp.tokenInfo?.logoURI || opp.token?.logoURI
  const marketType = getMarketType(opp)
  const curatorLogo = opp.curatorLogo || DEFAULT_CURATOR_LOGO
  const utilization = getUtilization(opp)
  const collateralOnly =
    (opp.sourceData.type === 'elend' || opp.sourceData.type === 'pooled') && opp.sourceData.isCollateralOnly === true
  const totalAssets = getTotalAssets(opp)
  const displaySymbol = getDisplayTokenSymbol(opp.chainId, TokenSymbol(opp.assetSymbol))

  const tvlUsd = opp.tvlUsd?.toNumber()
  const userDepositValue = opp.userDeposit?.toNumber()
  const userDepositUsdValue =
    opp.userDepositUsd?.toNumber() ??
    (opp.token && opp.userDeposit ? opp.token.toUSD(opp.userDeposit).toNumber() : undefined)
  const walletBalanceValue = opp.walletBalance?.toNumber()
  let walletBalanceUsdValue =
    opp.walletBalance && opp.token && opp.walletBalance.gt(0)
      ? opp.token.toUSD(opp.walletBalance).toNumber()
      : undefined
  if (opp.walletBreakdown && walletBalanceUsdValue !== undefined && walletBalanceUsdValue > 0) {
    const { nativeUsd, wrappedUsd } = opp.walletBreakdown
    if (nativeUsd > MIN_DISPLAY_USD || wrappedUsd > MIN_DISPLAY_USD) {
      walletBalanceUsdValue = nativeUsd + wrappedUsd
    }
  }

  const apy: MarketValue = {
    primary: `${opp.supplyAPY.toFixed(2)}%`,
    accent: 'gradient',
  }

  const apy30d: MarketValue = {
    primary: opp.supplyApy30d != null && opp.supplyApy30d > 0 ? `${opp.supplyApy30d.toFixed(2)}%` : '-',
    sortValue: opp.supplyApy30d ?? undefined,
  }

  const tvl: MarketValue = {
    primary: formatUsd(tvlUsd, true),
    secondary:
      totalAssets !== undefined && totalAssets > 0 ? formatTokenAmount(totalAssets, displaySymbol, 2) : undefined,
    sortValue: tvlUsd,
  }

  const utilizationValue: MarketValue = {
    primary: utilization !== undefined ? `${(utilization * 100).toFixed(1)}%` : '-',
  }

  const hasMeaningfulPosition = userDepositUsdValue !== undefined && userDepositUsdValue >= MIN_DISPLAY_USD

  const userPosition: MarketValue = {
    primary: hasMeaningfulPosition ? formatUsd(userDepositUsdValue, false) : '-',
    secondary:
      hasMeaningfulPosition && userDepositValue !== undefined
        ? formatTokenAmount(userDepositValue, displaySymbol, 4)
        : undefined,
    accent: hasMeaningfulPosition ? 'positive' : 'default',
  }

  const walletBalance: MarketValue = {
    primary:
      walletBalanceValue !== undefined && walletBalanceValue > 0
        ? formatTokenAmount(walletBalanceValue, displaySymbol, 4)
        : '-',
  }

  const depositOrLendContextPoints = opp.partnerPoints?.filter((p) => p.lend || p.deposit || p.collateral)
  const apyPoints: ApyPointsInfo | undefined = depositOrLendContextPoints?.length
    ? {
        partnerPoints: depositOrLendContextPoints,
        asset: opp.assetSymbol,
      }
    : undefined

  return {
    id: opp.id,
    href: getYieldOpportunityDetailsPath(opp),
    vaultAddress: opp.address,
    reserveStatus: opp.reserveStatus,
    fraxlendPauseState: opp.fraxlendPauseState,
    token: {
      symbol: displaySymbol,
      subtitle: marketType,
      logoSrc: tokenImage,
      logoFallbackSrc: fallbackLogo,
      curatorLogoSrc: curatorLogo,
      curatorName: opp.curatorName || 'HypurrFi',
    },
    apy,
    apy30d,
    apyBreakdown: getApyBreakdown(opp),
    apyPoints,
    apyMarketType: opp.source,
    collateralOnly,
    tvl,
    utilization: utilizationValue,
    exposure: toExposureVM(opp.exposure),
    rawExposure: opp.exposure,
    userPosition,
    walletBalance,
    walletBalanceAmount: walletBalanceValue !== undefined && walletBalanceValue > 0 ? walletBalanceValue : undefined,
    walletBalanceSymbol: walletBalanceValue !== undefined && walletBalanceValue > 0 ? displaySymbol : undefined,
    holdingsSortValue:
      ((userDepositUsdValue ?? 0) >= MIN_DISPLAY_USD ? (userDepositUsdValue ?? 0) : 0) +
      ((walletBalanceUsdValue ?? 0) >= MIN_DISPLAY_USD ? (walletBalanceUsdValue ?? 0) : 0),
  }
}

export function toMarketEntryViewModels(opportunities: YieldOpportunity[]): MarketEntryViewModel[] {
  return opportunities.map(toMarketEntryViewModel)
}
